import { useMemo, useState } from "react";

import {
    calculateUnwindStats,
    getLoopSlippage,
    LoopPositionExpanded,
    LoopTransferType,
    LoopUnwindParams,
    useActiveWallet,
    useLoopCollateralPrice,
    useLoopUnwindTransaction,
    useUnwindQuoteAndIxs,
    getZcCollateral,
    getZcLedger,
    LoopRouteResponseExpanded
} from "@bridgesplit/abf-react";
import {
    Result,
    LOADING_ERROR,
    formatUsd,
    MISSING_WALLET_ERROR,
    bsMath,
    formatTokenAmount,
    Hidable
} from "@bridgesplit/utils";
import {
    Column,
    MediaStack,
    MEDIA,
    OutlinedCard,
    Row,
    StatColumn,
    TagTextAlert,
    Text,
    TextButton,
    TooltipText,
    ToggleChevron,
    StatProps
} from "@bridgesplit/ui";
import { COPY } from "app/constants";
import { TrackTransactionEvent } from "app/types";
import { SwapHoriz } from "@mui/icons-material";
import { Collapse, Divider } from "@mui/material";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";

import { ActionProps } from "./types";
import {
    AppButton,
    Slippage,
    TokenAmountWithUsd,
    useLoopUnwindAlerts,
    useLoopDisclaimers,
    LoopDisplayType,
    ORACLE_DISCREPANCY_THRESHOLD,
    RefreshButton
} from "../../common";
import { useTransactionSender } from "../../transactions";
import { DisclaimerAlert } from "./common";

export default function LoopUnwind({ loopPosition }: ActionProps) {
    const { slippagePresetsUi, defaultSlippageUi } = getLoopSlippage(loopPosition?.loopExpanded);
    const { slippagePercentDecimals, slippageWrapperProps, slippageButtonProps } = Slippage.useController({
        defaultSlippageUi,
        presetsUi: slippagePresetsUi
    });

    const [displayType, setDisplayType] = useState<LoopDisplayType>(
        loopPosition?.loopExpanded.withdrawType === LoopTransferType.PrincipalOnly
            ? LoopDisplayType.UsdCostBasis
            : LoopDisplayType.TokenCostBasis
    );

    const unwind = useLoopUnwind();

    const {
        data: externalQuote,
        isError,
        isLoading: externalQuoteLoading,
        isFetching: externalQuoteFetching,
        refetch: refetchExternalQuote
    } = useUnwindQuoteAndIxs({
        loopPositionExpanded: loopPosition,
        slippagePercentDecimals: slippagePercentDecimals ?? 0
    });

    const { data: jupiterPrices } = useLoopCollateralPrice({
        tokens: loopPosition?.loopExpanded
            ? [loopPosition.loopExpanded.collateralToken, loopPosition.loopExpanded.principalToken]
            : undefined,
        options: { skip: !loopPosition }
    });

    const summary = useMemo(() => {
        if (!loopPosition || !jupiterPrices || externalQuoteLoading || externalQuoteFetching) return undefined;
        return calculateUnwindStats({ loopPosition, externalQuote, jupiterPrices });
    }, [loopPosition, jupiterPrices, externalQuoteLoading, externalQuote, externalQuoteFetching]);

    const { unwindWarningMessage } = useLoopUnwindAlerts({
        loopPositionExpanded: loopPosition,
        summary,
        displayType,
        variant: "route"
    });

    const disclaimer = useLoopDisclaimers({ loopExpanded: loopPosition?.loopExpanded });

    return (
        <Slippage.Wrapper {...slippageWrapperProps}>
            <Row spaceBetween>
                <Text variant="h4">{COPY.UNWIND_TERM}</Text>
                <Row spacing={0.5}>
                    <Slippage.Button {...slippageButtonProps} />
                    <RefreshButton refresh={refetchExternalQuote} />
                </Row>
            </Row>

            <ReceiveStats summary={summary} />
            <OutlinedCard>
                <SecondaryStats
                    loading={!summary}
                    loopPosition={loopPosition}
                    displayType={displayType}
                    setDisplayType={setDisplayType}
                    summary={summary}
                    slippagePercentDecimals={slippagePercentDecimals}
                />
            </OutlinedCard>
            {unwindWarningMessage ? (
                <TagTextAlert color="warning" icon="warning">
                    {unwindWarningMessage}
                </TagTextAlert>
            ) : null}
            {isError && (
                <TagTextAlert color="error" icon="warning">
                    Unable to find a swap route on Jupiter. Please check back later
                </TagTextAlert>
            )}
            <AppButton
                disabled={!summary || disclaimer?.disclaimerError() || isError}
                isTransaction
                asyncCta={{ onClickWithResult: () => unwind({ loopPosition, externalQuote }) }}
            >
                {COPY.UNWIND_TERM}
            </AppButton>
            {disclaimer && <DisclaimerAlert disclaimer={disclaimer} />}
        </Slippage.Wrapper>
    );
}

type Summary = ReturnType<typeof calculateUnwindStats> | undefined;

function ReceiveStats({ summary }: { summary: Summary }) {
    const tokenStats = summary?.tokenStats;
    return (
        <Column alignItems={"center"} spacing={0.5}>
            <Row spacing={0.5}>
                <Text variant="body2" color="caption">
                    Receive on close
                </Text>
                <TooltipText helpText="Estimated minimum you'll receive including any swap price deviation from repaying debt." />
            </Row>
            <TokenAmountWithUsd
                variant="h3"
                tokenSize="md"
                amount={tokenStats?.receiveTokenAmount}
                amountUsd={tokenStats?.receiveUsdAmount}
                token={tokenStats?.tokenToReceive}
            />
        </Column>
    );
}

function getPrincipalOnlyUnwindStats({ loopPosition }: { loopPosition: LoopPositionExpanded | undefined }) {
    const collateral = getZcCollateral(loopPosition?.loanExpanded);
    const collateralAmount = collateral?.loanCollateral.amount;
    const principalPrice = loopPosition?.loopExpanded.principalOracle.usdPrice;

    const collateralPrice = loopPosition?.loopExpanded.collateralOracle.usdPrice;

    const ledger = getZcLedger(loopPosition?.loanExpanded);

    const totalDebt = ledger?.ledgerDebt.total;

    return [
        {
            caption: "Collateral",
            value: (
                <MediaStack query={MEDIA.SM} sx={{ minWidth: "max-content", alignItems: "flex-end" }}>
                    <Text> {formatUsd(bsMath.mul(collateralPrice, collateralAmount))} </Text>
                    <Text color="caption">
                        {formatTokenAmount(collateralAmount, { symbol: collateral?.loanCollateral.metadata.symbol })}
                    </Text>
                </MediaStack>
            ),
            tooltip: "Total amount of collateral in the position"
        },
        {
            caption: "Debt to repay",
            value: (
                <MediaStack query={MEDIA.SM} sx={{ minWidth: "max-content", alignItems: "flex-end" }}>
                    <Text> {formatUsd(bsMath.mul(principalPrice, totalDebt))} </Text>
                    <Text color="caption">
                        {formatTokenAmount(totalDebt, { symbol: loopPosition?.loopExpanded.principalToken.symbol })}
                    </Text>
                </MediaStack>
            ),
            tooltip: "Total debt amount that will be repaid when unwinding"
        }
    ];
}

function getCollateralUnwindStats({
    loopPosition,
    displayType,
    setDisplayType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayType: (displayType: LoopDisplayType) => void;
}) {
    const collateral = getZcCollateral(loopPosition?.loanExpanded);
    const collateralAmount = collateral?.loanCollateral.amount;
    const collateralPrice = loopPosition?.loopExpanded.collateralOracle.usdPrice;
    const principalPrice = loopPosition?.loopExpanded.principalOracle.usdPrice;
    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const totalDebt = ledger?.ledgerDebt.total;
    const debtToRepayInDollars = bsMath.mul(principalPrice, totalDebt);
    const collateralAmountOfDebt = bsMath.div(debtToRepayInDollars, collateralPrice);

    return [
        {
            caption: "Collateral",
            value: (
                <Row spacing={0.5}>
                    {displayType === LoopDisplayType.UsdCostBasis ? (
                        <Text> {formatUsd(bsMath.mul(collateralPrice, collateralAmount))} </Text>
                    ) : (
                        <Text>
                            {formatTokenAmount(collateralAmount, {
                                symbol: collateral?.loanCollateral.metadata.symbol
                            })}
                        </Text>
                    )}

                    <TextButton
                        color="disabled"
                        onClick={() =>
                            setDisplayType(
                                displayType === LoopDisplayType.UsdCostBasis
                                    ? LoopDisplayType.TokenCostBasis
                                    : LoopDisplayType.UsdCostBasis
                            )
                        }
                    >
                        <SwapHoriz />
                    </TextButton>
                </Row>
            ),
            tooltip: "Total amount of collateral in the position"
        },
        {
            caption: "Debt to repay",
            value: (
                <MediaStack query={MEDIA.SM} sx={{ minWidth: "max-content", alignItems: "flex-end" }}>
                    {displayType === LoopDisplayType.UsdCostBasis ? (
                        <Text> {formatUsd(bsMath.mul(principalPrice, totalDebt))} </Text>
                    ) : (
                        <Text>
                            {formatTokenAmount(collateralAmountOfDebt, {
                                symbol: loopPosition?.loopExpanded.collateralToken.symbol
                            })}
                        </Text>
                    )}
                    <Text color="caption">
                        {formatTokenAmount(totalDebt, { symbol: loopPosition?.loopExpanded.principalToken.symbol })}
                    </Text>
                </MediaStack>
            ),
            tooltip: "Total debt amount that will be repaid when unwinding"
        }
    ];
}

function calculateFairMarketValue(loopPosition: LoopPositionExpanded | undefined): number | undefined {
    const collateral = getZcCollateral(loopPosition?.loanExpanded);
    const collateralAmount = collateral?.loanCollateral.amount;
    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const totalDebt = ledger?.ledgerDebt.total;

    const oraclePrincipalUsd = bsMath.mul(loopPosition?.loopExpanded.principalOracle.usdPrice, totalDebt);

    const oracleCollateralUsd = bsMath.mul(loopPosition?.loopExpanded.collateralOracle.usdPrice, collateralAmount);

    const fairMarketValue = bsMath.sub(oracleCollateralUsd, oraclePrincipalUsd);

    return fairMarketValue;
}

function SecondaryStats({
    loading,
    loopPosition,
    displayType,
    setDisplayType,
    summary,
    slippagePercentDecimals
}: {
    loading: boolean;
    loopPosition: LoopPositionExpanded | undefined;
    displayType: LoopDisplayType;
    setDisplayType: (displayType: LoopDisplayType) => void;
    summary: Summary | undefined;
    slippagePercentDecimals: number | undefined;
}) {
    const unwindStats = useMemo(() => {
        if (loopPosition?.loopExpanded.withdrawType === LoopTransferType.PrincipalOnly) {
            return getPrincipalOnlyUnwindStats({ loopPosition });
        }
        return getCollateralUnwindStats({ loopPosition, displayType, setDisplayType });
    }, [loopPosition, displayType, setDisplayType]);

    // this is the total impact of the swap, including fees, slippage, price impact, and oracle price deviation
    const executionDetails = useExecutionDetails({ loopPosition, summary, slippagePercentDecimals });

    return (
        <Column spacing={1}>
            <StatColumn spacing={1} stats={unwindStats} loading={loading} />
            <ExecutionDetails
                loopPosition={loopPosition}
                displayType={displayType}
                executionDetails={executionDetails}
                loading={loading}
                summary={summary}
                slippagePercentDecimals={slippagePercentDecimals}
            />
        </Column>
    );
}

interface ExecutionDetails {
    receiveUsdAmount: number;
    executionLossAmount: number;
    priceImpactAmount: number;
    slippageAmount: number;
    fairMarketValue: number;
    feeAmount: number;
    oraclePriceDeviation: number;
}

function useExecutionDetails({
    loopPosition,
    summary,
    slippagePercentDecimals
}: {
    loopPosition: LoopPositionExpanded | undefined;
    summary: Summary | undefined;
    slippagePercentDecimals: number | undefined;
}): ExecutionDetails | undefined {
    // Calculate execution loss details

    const fairMarketValue = calculateFairMarketValue(loopPosition);

    const executionDetails = useMemo(() => {
        if (!loopPosition || !summary?.tokenStats || fairMarketValue === undefined) return undefined;

        const { tokenStats } = summary;

        const receiveUsdAmount = tokenStats.receiveUsdAmount;

        // Calculate fee impact
        const feeAmount = summary.feeDecimals ? summary.feeDecimals * receiveUsdAmount : 0;

        // Calculate price impact
        const priceImpactAmount = summary.priceImpactDecimals ? summary.priceImpactDecimals * receiveUsdAmount : 0;

        // Calculate slippage
        const slippageAmount = slippagePercentDecimals ? slippagePercentDecimals * fairMarketValue : 0;

        const oraclePriceDeviation =
            fairMarketValue - receiveUsdAmount - feeAmount - priceImpactAmount - slippageAmount;

        const executionLossAmount = receiveUsdAmount - fairMarketValue;

        return {
            receiveUsdAmount,
            executionLossAmount,
            feeAmount,
            priceImpactAmount,
            slippageAmount,
            fairMarketValue,
            oraclePriceDeviation
        };
    }, [loopPosition, summary, slippagePercentDecimals, fairMarketValue]);

    return executionDetails;
}

function ExecutionDetails({
    loopPosition,
    executionDetails,
    displayType,
    summary,
    slippagePercentDecimals,
    loading
}: {
    loopPosition: LoopPositionExpanded | undefined;
    executionDetails: ExecutionDetails | undefined;
    displayType: LoopDisplayType;
    summary: Summary | undefined;
    slippagePercentDecimals: number | undefined;
    loading: boolean;
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const executionDetailsStats = useExecutionDetailsStats({
        loopPosition,
        summary,
        slippagePercentDecimals,
        displayType
    });

    if (executionDetailsStats.length === 0) return null;

    return (
        <Column>
            <Row sx={{ cursor: "pointer" }} spaceBetween onClick={() => setIsExpanded(!isExpanded)}>
                <Row spacing={1}>
                    <Text color="caption" variant="body1" loading={loading}>
                        Execution Impact
                    </Text>

                    {!loading && (
                        <TooltipText helpText="The difference between the executed swap price and the oracle price, inclusive of AMM fees, slippage, and price impact when swapping collateral to repay debt" />
                    )}
                </Row>
                <ExecutionDetailsEndValue
                    loading={loading}
                    executionDetails={executionDetails}
                    displayType={displayType}
                    isExpanded={isExpanded}
                    loopPosition={loopPosition}
                />
            </Row>

            <Collapse in={isExpanded}>
                <Column sx={{ mt: 1 }} spacing={1}>
                    <Divider />
                    <StatColumn spacing={1} stats={executionDetailsStats} />
                </Column>
            </Collapse>
        </Column>
    );
}

function ExecutionDetailsEndValue({
    loading,
    executionDetails,
    displayType,
    loopPosition,
    isExpanded
}: {
    loading: boolean;
    executionDetails: ExecutionDetails | undefined;
    displayType: LoopDisplayType;
    loopPosition: LoopPositionExpanded | undefined;
    isExpanded: boolean;
}) {
    return (
        <Row spacing={1}>
            <Row spacing={1}>
                <Text
                    loading={loading}
                    color={
                        executionDetails?.executionLossAmount && executionDetails.executionLossAmount < 0
                            ? "error"
                            : "success"
                    }
                >
                    {formatExecutionLossValue({
                        executionDetails,
                        displayType,
                        loopPosition
                    })}
                </Text>
            </Row>
            {!loading && <ToggleChevron textVariant="body1" sx={{ p: 0 }} expanded={isExpanded} />}
        </Row>
    );
}

function useExecutionDetailsStats({
    loopPosition,
    summary,
    slippagePercentDecimals,
    displayType
}: {
    loopPosition: LoopPositionExpanded | undefined;
    summary: Summary | undefined;
    slippagePercentDecimals: number | undefined;
    displayType: LoopDisplayType;
}) {
    const executionDetails = useExecutionDetails({ loopPosition, summary, slippagePercentDecimals });

    const executionDetailsStats = useMemo(() => {
        if (!executionDetails) return [];

        const collateralToken = loopPosition?.loopExpanded.collateralToken;
        const collateralPrice = loopPosition?.loopExpanded.collateralOracle.usdPrice || 1;

        const stats: Hidable<StatProps>[] = [
            {
                caption: "Fair Market Value",
                value: (
                    <Text>
                        {formatTokenValue(
                            executionDetails.fairMarketValue || 0,
                            displayType,
                            collateralPrice,
                            collateralToken
                        )}
                    </Text>
                ),
                tooltip: "Value based on oracle price"
            },
            {
                caption: "Fees",
                value: (
                    <Text color="error">
                        {formatTokenValue(-executionDetails.feeAmount, displayType, collateralPrice, collateralToken)}
                    </Text>
                ),

                tooltip: "AMM and external platform swap fees",
                hide: !executionDetails.feeAmount
            },
            {
                caption: "Price Impact",
                value: (
                    <Text color="error">
                        {formatTokenValue(
                            -executionDetails.priceImpactAmount,
                            displayType,
                            collateralPrice,
                            collateralToken
                        )}
                    </Text>
                ),
                tooltip: "Market price impact of the swap",
                hide: !executionDetails.priceImpactAmount
            },
            {
                caption: "Slippage",
                value: (
                    <Text color="error">
                        {formatTokenValue(
                            -executionDetails.slippageAmount,
                            displayType,
                            collateralPrice,
                            collateralToken
                        )}
                    </Text>
                ),
                tooltip: "Maximum allowed price deviation during execution",
                hide: !executionDetails.slippageAmount
            },
            {
                caption: "Oracle Price Deviation",
                value: (
                    <Text color={executionDetails.oraclePriceDeviation <= 0 ? "success" : "error"}>
                        {formatTokenValue(
                            -executionDetails.oraclePriceDeviation,
                            displayType,
                            collateralPrice,
                            collateralToken
                        )}
                    </Text>
                ),
                tooltip: "Difference between market price and oracle price, excluding fees, slippage, and price impact",
                hide: !Math.abs(executionDetails.oraclePriceDeviation)
            },
            {
                caption: "Execution Value",
                value: (
                    <Text>
                        {formatTokenValue(
                            executionDetails.receiveUsdAmount || 0,
                            displayType,
                            collateralPrice,
                            collateralToken
                        )}
                    </Text>
                ),
                tooltip: "Final execution value"
            }
        ].filter((s) => !s.hide);

        return stats;
    }, [executionDetails, displayType, loopPosition]);

    return executionDetailsStats;
}

function useLoopUnwind() {
    const unwind = useLoopUnwindTransaction();
    const send = useTransactionSender();
    const { activeWallet } = useActiveWallet();
    return async ({
        loopPosition,
        externalQuote
    }: {
        loopPosition: LoopPositionExpanded | undefined;
        externalQuote: LoopRouteResponseExpanded | undefined;
    }) => {
        if (!loopPosition || !loopPosition.loanExpanded || !externalQuote) return Result.errFromMessage(LOADING_ERROR);
        if (!activeWallet) return Result.errFromMessage(MISSING_WALLET_ERROR);
        const { loopExpanded, loanExpanded } = loopPosition;

        const receiveToken =
            loopExpanded.withdrawType === LoopTransferType.CollateralOnly
                ? loopExpanded.collateralToken
                : loopExpanded.principalToken;

        const params: LoopUnwindParams = {
            loanAddress: loanExpanded.loan.address,
            principalMint: loopExpanded.principalToken.mint,
            collateralMint: loopExpanded.collateralToken.mint,
            mintToReceive: receiveToken.mint,
            userPublicKey: activeWallet,
            swapIx: externalQuote.swapIxs,
            swapLuts: externalQuote.swapLuts
        };

        return await send(unwind, params, {
            alerter: {
                generationEstimateSeconds: 8,
                showProgress: true
            },
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitCloseEarnOrder,
                params
            }
        });
    };
}

export function hasPnlOracleDiscrepancy({
    oraclePnlUsd,
    marketPnlUsd
}: {
    oraclePnlUsd: number | undefined;
    marketPnlUsd: number | undefined;
}) {
    if (oraclePnlUsd === undefined || marketPnlUsd === undefined) return false;

    const percentDiff = (marketPnlUsd - oraclePnlUsd) / (Math.abs(oraclePnlUsd) || 1);
    return Math.abs(percentDiff) > ORACLE_DISCREPANCY_THRESHOLD;
}

function formatExecutionLossValue({
    executionDetails,
    displayType,
    loopPosition
}: {
    executionDetails: ExecutionDetails | undefined;
    displayType: LoopDisplayType;
    loopPosition: LoopPositionExpanded | undefined;
}) {
    if (!executionDetails?.executionLossAmount) return "--";

    const collateralPrice = loopPosition?.loopExpanded.collateralOracle.usdPrice || 1;
    const collateralToken = loopPosition?.loopExpanded.collateralToken;

    return formatTokenValue(executionDetails.executionLossAmount, displayType, collateralPrice, collateralToken);
}

function formatTokenValue(
    usdAmount: number,
    displayType: LoopDisplayType,
    collateralPrice: number,
    collateralToken: TokenListMetadata | undefined
) {
    if (displayType === LoopDisplayType.TokenCostBasis) {
        const tokenAmount = bsMath.div(usdAmount, collateralPrice);
        return formatTokenAmount(tokenAmount, {
            symbol: collateralToken?.symbol,
            short: true
        });
    }
    return formatUsd(usdAmount);
}
